// backend/live_web/src/pages/SessionDetailPage.tsx
/**
 * One session, live or finished: who the patient is, the consultation with Jiva's differential
 * beside it answer by answer, the final differential it ended on, and the raw voice events —
 * laid out in the eval dashboard's session language.
 *
 * The page polls every 3s while the session is active, so turns arrive while it is read. The
 * differential panel follows the SCROLL (`useAnchoredTurn`): it is always about the exchange
 * being read. On an active session it also follows the CONSULTATION — when a new answer's
 * belief lands and the reader was on the newest one, the page moves to it and the chart glides
 * there. A reader who has scrolled back to an earlier answer is left where they are.
 */

import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "react-router-dom";

import { getSessionDetail } from "../api/admin";
import { ApiError } from "../api/client";
import type { AdminSessionDetail } from "../api/types";
import FinalDifferential from "../components/session/FinalDifferential";
import RawEvents from "../components/session/RawEvents";
import SessionHeader from "../components/session/SessionHeader";
import Transcript from "../components/session/Transcript";
import TurnDifferentialPanel from "../components/session/TurnDifferentialPanel";
import { knownSoFar } from "../components/session/knownSoFar";
import {
  inTurnOrder,
  indexBeliefs,
  resolveExchangeBelief,
  steppableTurns,
} from "../components/session/liveBeliefs";
import { useAnchoredTurn } from "../components/session/useAnchoredTurn";
import { usePendingTurn } from "../components/session/usePendingTurn";
import { buttonClass } from "../components/ui/buttonStyles";
import { Card, CardHeading } from "../components/ui/Card";
import ErrorState from "../components/ui/ErrorState";
import LoadingState from "../components/ui/LoadingState";
import NoticeCard from "../components/ui/NoticeCard";
import { count, plural } from "../lib/format";
import { introduction } from "../lib/patient";

export default function SessionDetailPage() {
  const { id } = useParams<{ id: string }>();

  const query = useQuery({
    queryKey: ["admin-session-detail", id],
    queryFn: () => getSessionDetail(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.status === "active" ? 3000 : false),
    refetchIntervalInBackground: false,
  });

  if (query.isError && query.error instanceof ApiError) {
    if (query.error.status === 403) return <Navigate to="/not-authorized" replace />;
    if (query.error.status === 404) {
      return (
        <NoticeCard
          title="Session not found"
          action={
            <Link to="/sessions" className={buttonClass("outline", "sm")}>
              Back to sessions
            </Link>
          }
        >
          No session with this id exists, or it is not visible to your account.
        </NoticeCard>
      );
    }
  }

  // A failed background refetch keeps the last good render on screen; only a first load
  // that failed has nothing to show.
  if (query.data === undefined) {
    return (
      <Card>
        {query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <LoadingState label="Loading the session…" />
        )}
      </Card>
    );
  }

  // Keyed on the session, so moving to another one remounts the view and its anchor.
  return <SessionView key={query.data.id} detail={query.data} />;
}

/**
 * The loaded session. Split from the query shell because everything below needs the loaded
 * rows, and hooks cannot be called conditionally.
 */
function SessionView({ detail }: { detail: AdminSessionDetail }) {
  const active = detail.status === "active";
  const turns = useMemo(() => inTurnOrder(detail.turns), [detail.turns]);

  // Every answer's belief, and which exchanges the panel can be about: those whose answer
  // stored one (every answer, on a finished session that stored none). An inferred row has no answer of its own, so the arrows and the scroll pass it.
  const beliefs = useMemo(() => indexBeliefs(turns), [turns]);
  const steppable = useMemo(() => steppableTurns(beliefs, active), [beliefs, active]);
  const pending = usePendingTurn(beliefs, active);

  const transcriptRef = useRef<HTMLDivElement>(null);
  const { anchoredIndex, scrollToTurn } = useAnchoredTurn(transcriptRef, steppable);
  const anchored = anchoredIndex ?? steppable[0] ?? null;
  const prevTurn = anchored === null ? undefined : steppable.filter((n) => n < anchored).at(-1);
  const nextTurn = anchored === null ? undefined : steppable.find((n) => n > anchored);

  const belief = useMemo(() => resolveExchangeBelief(beliefs, anchored), [beliefs, anchored]);
  const known = useMemo(
    () => (anchored === null ? [] : knownSoFar(turns, anchored)),
    [turns, anchored],
  );

  // Follow the live consultation: when a new belief lands and the reader was on the newest
  // one (or on none yet), move to it. Opening an active session lands on its newest answer; a
  // finished one opens at the top, as a transcript is read.
  const latest = steppable.at(-1);
  const seenLatest = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (latest === undefined || latest === seenLatest.current) return;
    const was = seenLatest.current;
    seenLatest.current = latest;
    if (!active) return;
    if (was === undefined || anchoredIndex === null || anchoredIndex === was) scrollToTurn(latest);
  }, [latest, active, anchoredIndex, scrollToTurn]);

  // The column is reserved on an active session before its first belief, so the panel does not
  // pop in mid-consult; a finished session with no answers has no panel to show.
  const showPanel = active || steppable.length > 0;

  const asked = turns.filter((turn) => !turn.is_inferred && turn.turn_number > 0).length;
  const inferred = turns.filter((turn) => turn.is_inferred).length;
  const caption = [
    `${plural(asked, "question")} asked`,
    `${count(inferred)} answered without asking`,
    active ? "in progress" : null,
  ]
    .filter((part) => part !== null)
    .join(" · ");

  return (
    <div className="flex flex-col gap-4">
      <SessionHeader detail={detail} />

      <Card>
        <CardHeading title="The consultation" caption={caption} />
        <div className="flex flex-col gap-7 px-7 pb-6">
          {/* Side by side from `lg`, the panel narrower until `xl`; below that the panel
              stacks under the transcript, as on the eval dashboard. */}
          <div
            className={
              showPanel
                ? "grid gap-5 lg:grid-cols-[minmax(0,1fr)_27rem] xl:grid-cols-[minmax(0,1fr)_minmax(0,36rem)]"
                : ""
            }
          >
            {/* On a finished session, room below the last exchange (an active one has its live
                tail): the sticky panel cannot pass the column's end, so this keeps it whole
                while the last exchange is read in the reading band, a third of the way down. It
                also lets that exchange reach the band on a short page. */}
            <div
              ref={transcriptRef}
              className={showPanel && !active ? "lg:pb-[calc(50vh-15.75rem)]" : undefined}
            >
              <Transcript
                turns={turns}
                anchoredTurn={showPanel ? anchored : null}
                pendingTurn={pending}
                intro={introduction(detail.patient.name, detail.patient.age, detail.patient.sex)}
                active={active}
              />
            </div>

            {/* Centred in the viewport rather than pinned to its top: the panel is short, the
                transcript beside it long, and a top-pinned card sat opposite an exchange the
                reader had already scrolled past. Only the panel is sticky, offset by half its
                own 31.5rem height, so at the transcript's end it lines up with the last
                exchange; a viewport-tall centring box rode half a screen above it. */}
            {showPanel ? (
              <div className="min-w-0 lg:sticky lg:top-[max(1rem,calc(50vh-15.75rem))] lg:self-start">
                <TurnDifferentialPanel
                  belief={belief}
                  known={known}
                  active={active}
                  pendingAnswer={pending}
                  noBeliefs={!active && beliefs.answers.every((a) => a.differential === null)}
                  onPrev={prevTurn === undefined ? undefined : () => scrollToTurn(prevTurn)}
                  onNext={nextTurn === undefined ? undefined : () => scrollToTurn(nextTurn)}
                />
              </div>
            ) : null}
          </div>

          {/* What the consultation ended on reads after the conversation it concludes. */}
          <FinalDifferential detail={detail} />

          <RawEvents detail={detail} />
        </div>
      </Card>
    </div>
  );
}
